/**
 * TestVamProtocolBlock — Sprint 11 S11.3 (motor v8 §2).
 *
 * Pantalla del Test VAM con copy oficial v8: hero + 6 pasos numerados
 * + caja consejo. REEMPLAZA el render del structure_json para
 * `session_type='test_vam'` (no coexisten — el structure_json del
 * catálogo S6/S9.5 queda obsoleto frente al copy v8).
 *
 * El paso 5 incluye la fecha formateada DD/MM/AAAA derivada de
 * `session.session_date` para la nomenclatura obligatoria Strava
 * "TEST VAM (DD/MM/AAAA)". Esa nomenclatura habilita el wire
 * Strava → update_athlete_from_vam_test que llega en S11.4.
 *
 * Decisiones de diseño:
 * - Sub-bloque visual aparte del SessionDetailPage para que el copy
 *   v8 (mucho más largo que un structure_json normal) tenga su propio
 *   contenedor con padding y borde.
 * - Los 6 pasos se renderizan con número grande visible + título +
 *   body. Sin iconos extra (theme actual no tiene set de icons
 *   numerados; basta con tipografía).
 * - Caja consejo en color `warning` (naranja suave del theme, mismo
 *   que el warning del Posponer modal — coherencia visual de
 *   "atención sin ser error").
 *
 * Props:
 *   session: TrainingSession del test_vam (planned). Se usa
 *     session.session_date para formatear la fecha en paso 5.
 */
import { formatDateForVamLabel } from "@/lib/format";
import { t } from "@/i18n/es";
import type { TrainingSession } from "@/types/api";


interface ProtocolStep {
  number: number;
  title: string;
  body: string;
}

export interface TestVamProtocolBlockProps {
  session: TrainingSession;
}

export function TestVamProtocolBlock({ session }: TestVamProtocolBlockProps) {
  // Paso 5 incluye la fecha en formato DD/MM/AAAA derivada de
  // session.session_date. El template tiene placeholder {date}.
  const dateLabel = formatDateForVamLabel(session.session_date);
  const step5Body = t.plan.testVamStep5BodyTemplate.replace("{date}", dateLabel);

  const steps: ProtocolStep[] = [
    {
      number: 1,
      title: t.plan.testVamStep1Title,
      body: t.plan.testVamStep1Body,
    },
    {
      number: 2,
      title: t.plan.testVamStep2Title,
      body: t.plan.testVamStep2Body,
    },
    {
      number: 3,
      title: t.plan.testVamStep3Title,
      body: t.plan.testVamStep3Body,
    },
    {
      number: 4,
      title: t.plan.testVamStep4Title,
      body: t.plan.testVamStep4Body,
    },
    {
      number: 5,
      title: t.plan.testVamStep5Title,
      body: step5Body,
    },
    {
      number: 6,
      title: t.plan.testVamStep6Title,
      body: t.plan.testVamStep6Body,
    },
  ];

  return (
    <section
      data-testid="test-vam-protocol-block"
      className="flex flex-col gap-5"
      aria-labelledby="test-vam-hero-title"
    >
      {/* Hero — título grande + descripción + sub-descripción */}
      <header className="flex flex-col gap-3 rounded-md border border-lime/30 bg-lime/5 p-4">
        <h2
          id="test-vam-hero-title"
          data-testid="test-vam-hero-title"
          className="text-base font-medium text-bone"
        >
          {t.plan.testVamHeroTitle}
        </h2>
        <p
          data-testid="test-vam-hero-description"
          className="text-sm text-bone"
        >
          {t.plan.testVamHeroDescription}
        </p>
        <p className="text-sm text-bone-muted">
          {t.plan.testVamHeroSubDescription}
        </p>
      </header>

      {/* 6 pasos numerados */}
      <div className="flex flex-col gap-3">
        <h3 className="text-xs uppercase tracking-widest text-gray-mid">
          {t.plan.testVamStepsTitle}
        </h3>
        <ol
          data-testid="test-vam-steps"
          className="flex flex-col gap-3"
        >
          {steps.map((step) => (
            <li
              key={step.number}
              data-testid={`test-vam-step-${step.number}`}
              className="flex gap-3 rounded-md border border-border-subtle bg-carbon-raised p-3"
            >
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-lime/50 bg-lime/10 text-sm font-medium text-lime"
              >
                {step.number}
              </span>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-bone">
                  {step.title}
                </p>
                <p
                  data-testid={`test-vam-step-${step.number}-body`}
                  className="text-sm text-bone-muted"
                >
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Caja consejo (warning soft — atención sin ser error) */}
      <aside
        data-testid="test-vam-tip"
        className="rounded-md border border-warning/30 bg-warning/10 p-4 text-sm"
      >
        <p className="font-medium text-warning">
          {t.plan.testVamTipTitle}
        </p>
        <p className="mt-1 text-bone-muted">{t.plan.testVamTipBody}</p>
      </aside>
    </section>
  );
}
